/* eslint-disable consistent-return */
import LoadingButton from '@mui/lab/LoadingButton';
import { m } from 'framer-motion';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import {
  type KeyboardEvent,
  type ReactNode,
  type WheelEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Controller, useWatch } from 'react-hook-form';
import Cropper, { type Area } from 'react-easy-crop';
import { NumericFormat } from 'react-number-format';
import { RHFAutocomplete, RHFSelect, RHFTextField } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/form-provider';
import ClearIcon from '@mui/icons-material/Clear';
import Iconify from 'src/components/iconify';
import { alpha, useTheme } from '@mui/material/styles';
import { useResponsive } from 'src/hooks/use-responsive';
import { HEADER } from 'src/layouts/config-layout';
import type { BenefitDetailsFormValues, useBenefitsDetails } from '../benefits-details.hook';
import RHFEditor from 'src/components/hook-form/rhf-editor';
import { RHFUploadSingleFileAccelerator } from 'src/components/hook-form/rhf-upload';
import RHFRadioGroupLegacy from 'src/components/hook-form/rhf-radio-group-legacy';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useMyDataStore } from 'src/store/me/me.store';
import BenefitsLivePreview from './benefits-live-preview';
import BenefitsEmptyLivePreview from './benefits-empty-live-preview';

type BenefitsDetailsHook = ReturnType<typeof useBenefitsDetails>;

type Props = {
  hook: BenefitsDetailsHook;
};

type SectionKey = 'display' | 'info' | 'redeem' | 'feedback';

type LivePreviewPanelProps = {
  control: BenefitsDetailsHook['control'];
  themeOptions: { value: string; label: string }[];
  companyOptions: { value: string; label: string; image?: string | null }[];
  locationOptions: { value: string; label: string; image?: string | null; location?: string }[];
};

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedFile = async (imageSrc: string, crop: Area, fileName: string): Promise<File> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas context not available');
  }

  canvas.width = crop.width;
  canvas.height = crop.height;

  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to create cropped image blob'));
          return;
        }

        const baseName = fileName.replace(/\.[^/.]+$/, '') || 'benefit-image';
        const outputFile = new File([blob], `${baseName}-crop.jpg`, {
          type: blob.type || 'image/jpeg',
        });

        (outputFile as File & { preview?: string }).preview = URL.createObjectURL(outputFile);
        resolve(outputFile);
      },
      'image/jpeg',
      0.92
    );
  });
};

function LivePreviewPanel({
  control,
  themeOptions,
  companyOptions,
  locationOptions,
}: LivePreviewPanelProps) {
  const isVivaEnv = useWatch({ control, name: 'exhibitionEnv' }) === 'viva';
  const watchedValues = useWatch({ control });
  const [debouncedPreviewValues, setDebouncedPreviewValues] = useState<BenefitDetailsFormValues>(
    watchedValues as BenefitDetailsFormValues
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedPreviewValues(watchedValues as BenefitDetailsFormValues);
    }, 180);

    return () => {
      window.clearTimeout(timer);
    };
  }, [watchedValues]);

  if (!isVivaEnv) {
    return <BenefitsEmptyLivePreview />;
  }

  return (
    <BenefitsLivePreview
      values={debouncedPreviewValues as BenefitDetailsFormValues}
      themeOptions={themeOptions}
      companyOptions={companyOptions}
      locationOptions={locationOptions}
    />
  );
}

function SectionTitle({ index, title }: { index: number; title: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'common.white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
        }}
      >
        {index}
      </Box>
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
    </Stack>
  );
}

export default function BenefitsDetailsForm({ hook }: Props) {
  const MAX_NUMERIC_FIELD_VALUE = 999999999;
  const BLOCKED_INTEGER_INPUT_KEYS = ['e', 'E', '+', '-', ',', '.'];
  const theme = useTheme();
  const permissions = useMyDataStore((state) => state.me?.cnAdminInfo?.permissions?.benefits);
  const lgUp = useResponsive('up', 'lg');
  const stickyTop = (lgUp ? HEADER.H_DESKTOP_OFFSET : HEADER.H_MOBILE) + 8;
  const actionBarAnchorRef = useRef<HTMLDivElement | null>(null);
  const stickyActionBarRef = useRef<HTMLDivElement | null>(null);
  const [isActionBarPinned, setIsActionBarPinned] = useState(false);
  const [actionBarFrame, setActionBarFrame] = useState({ left: 0, width: 0, height: 0 });
  const submitActionAnchorRef = useRef<HTMLDivElement | null>(null);
  const submitActionBarRef = useRef<HTMLDivElement | null>(null);
  const [isSubmitActionPinned, setIsSubmitActionPinned] = useState(false);
  const [submitActionFrame, setSubmitActionFrame] = useState({ left: 0, width: 0, height: 0 });
  const [customerVoiceEmbedScript, setCustomerVoiceEmbedScript] = useState('');
  const [isCustomerVoiceScriptDialogOpen, setIsCustomerVoiceScriptDialogOpen] = useState(false);
  const [isScriptInstructionsPreviewOpen, setIsScriptInstructionsPreviewOpen] = useState(false);
  const scriptInstructionsPreviewAnchorRef = useRef<HTMLDivElement | null>(null);
  const [isCropDialogOpen, setIsCropDialogOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [cropFileName, setCropFileName] = useState('benefit-image');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>({
    display: true,
    info: true,
    redeem: true,
    feedback: true,
  });
  const submitFloatingBottom = lgUp ? 24 : 16;

  const benefitType = useWatch({ control: hook.control, name: 'benefitType' });
  const exhibitionEnv = useWatch({ control: hook.control, name: 'exhibitionEnv' });
  const benefitForNewUsers = useWatch({ control: hook.control, name: 'benefitForNewUsers' });
  const privateValue = useWatch({ control: hook.control, name: 'private' });
  const localType = useWatch({ control: hook.control, name: 'localType' });
  const highlightCategory = useWatch({ control: hook.control, name: 'highlightCategory' });
  const rewardType = useWatch({ control: hook.control, name: 'rewardType' });
  const newUsersPeriod = useWatch({ control: hook.control, name: 'newUsersPeriod' });
  const show = useWatch({ control: hook.control, name: 'show' });
  const showTo = useWatch({ control: hook.control, name: 'showTo' });
  const redeemCodeQuantity = useWatch({ control: hook.control, name: 'redeemCodeQuantity' });
  const hasCustomerVoice = useWatch({ control: hook.control, name: 'hasCustomerVoice' });
  const imageValue = useWatch({ control: hook.control, name: 'image' });

  const isCoupon = benefitType === 'coupon';
  const isVivaEnv = exhibitionEnv === 'viva';
  const isLpEventsEnv = exhibitionEnv === 'lp_events';
  const isWhatsAppEnv = exhibitionEnv === 'whatsapp';
  const isRewardEnv = exhibitionEnv === 'reward';
  const isParkingEnv = exhibitionEnv === 'parking';
  const defaultInformationDateColumnSize = isLpEventsEnv ? 6 : 4;
  const informationDateColumnSize = isParkingEnv ? 3 : defaultInformationDateColumnSize;
  const isRewardCoupon = isCoupon && isRewardEnv;
  const isWhatsAppProduct = !isCoupon && isWhatsAppEnv;
  const isFeedbackFormAvailableEnv = isVivaEnv || isLpEventsEnv || isRewardEnv;
  const cropAspect = isCoupon ? 375 / 150 : 325 / 150;
  const showNewUsersPeriod = Boolean(benefitForNewUsers);
  const isPrivate = String(privateValue) === 'true';
  const isLocalTypeCompany = localType !== 'local';
  const isLpEventsHomeHighlighted = Array.isArray(highlightCategory)
    ? highlightCategory.includes('lp_events_home_highlights')
    : false;
  const isValidNewUsersPeriod =
    new Date(newUsersPeriod?.endDate ?? new Date()).getTime() >=
    new Date(newUsersPeriod?.startDate ?? new Date()).getTime();
  const previousLocalType = useRef(localType);
  const previousBenefitType = useRef(benefitType);
  const selectedRedeemName =
    hook.currentRedeem?.label || hook.currentRedeem?.details?.name || hook.currentRedeem?.name;
  const selectedPartnerName =
    hook.currentRedeem?.partnerId?.name || hook.currentRedeem?.partner?.name;
  const selectedRedeemImage =
    Array.isArray(hook.currentRedeem?.details?.images) && hook.currentRedeem?.details?.images.length
      ? hook.currentRedeem.details.images[hook.currentRedeem.details.images.length - 1]
      : '';
  const hasRegisteredCpfs =
    hook.isEdit && hook.cpfs?.some((cpf) => Boolean(Array.isArray(cpf) ? cpf?.[0] : cpf));
  const hasRegisteredCodes = hook.isEdit && hook.codes?.some(Boolean);
  const limitCaptionSx = {
    minHeight: { xs: 'auto', md: 38 },
    display: 'flex',
    alignItems: 'flex-end',
    lineHeight: 1.35,
  };

  const setFieldFromRedeem = (fieldName: string, value: any) => {
    hook.methods.setValue(fieldName as any, value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const getWebpFileName = (value?: string) => {
    const fallbackName = 'benefit-image';
    const rawFileName = value?.trim() || fallbackName;
    const fileName = rawFileName.split(/[\\/]/).pop()?.split(/[?#]/)[0] || fallbackName;

    return /\.[a-z0-9]+$/i.test(fileName) ? fileName : `${fileName}.webp`;
  };

  const getRemoteImageFileName = (url: string) => {
    try {
      const urlFileName = decodeURIComponent(new URL(url).pathname.split('/').pop() || '');
      return getWebpFileName(urlFileName);
    } catch {
      return getWebpFileName();
    }
  };

  const handleDownloadImage = async () => {
    if (!imageValue) {
      return;
    }

    const isFile = imageValue instanceof File;
    const fileName = isFile
      ? getWebpFileName(imageValue.name)
      : getRemoteImageFileName(String(imageValue));
    let downloadUrl: string | null = null;

    try {
      if (isFile) {
        downloadUrl = URL.createObjectURL(imageValue);
      } else {
        const response = await fetch(String(imageValue));
        if (!response.ok) {
          throw new Error('Failed to fetch image');
        }
        const blob = await response.blob();
        const webpBlob =
          blob.type === 'image/webp' ? blob : new Blob([blob], { type: 'image/webp' });
        downloadUrl = URL.createObjectURL(webpBlob);
      }

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error(error);
    } finally {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    }
  };

  const applyCustomerVoiceEmbedScript = (rawScript: string) => {
    const script = rawScript.trim();

    if (!script) {
      return false;
    }

    const surveyEmbedMatch = script.match(
      /new\s+SurveyEmbed\s*\(\s*['"]([^'"]+)['"]\s*,\s*['"]([^'"]+)['"]\s*,\s*['"]([^'"]+)['"]/i
    );

    const formId = surveyEmbedMatch?.[1]?.trim();
    const baseUrl = surveyEmbedMatch?.[2]?.trim();
    const assetsUrl = surveyEmbedMatch?.[3]?.trim();

    if (!formId && !baseUrl && !assetsUrl) {
      return false;
    }

    hook.methods.setValue('hasCustomerVoice', true, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (baseUrl) {
      hook.methods.setValue('customerVoiceData.baseUrl', baseUrl, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    if (assetsUrl) {
      hook.methods.setValue('customerVoiceData.assetsUrl', assetsUrl, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    if (formId) {
      hook.methods.setValue('customerVoiceData.customerVoiceFormId', formId, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    return true;
  };

  const renderRedeemCopyIcon = (value: unknown, onClick: () => void) => {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    return (
      <IconButton size="small" onClick={onClick} sx={{ p: 0.4, color: 'primary.main' }}>
        <ContentCopyRoundedIcon fontSize="small" />
      </IconButton>
    );
  };

  const toggleSection = (section: SectionKey) => {
    setExpandedSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  const handleImageDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];

    if (!file) {
      return;
    }

    if (cropImageSrc) {
      URL.revokeObjectURL(cropImageSrc);
    }

    const previewUrl = URL.createObjectURL(file);
    setCropFileName(file.name || 'benefit-image');
    setCropImageSrc(previewUrl);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setIsCropDialogOpen(true);
  };

  const handleCloseCropDialog = () => {
    if (cropImageSrc) {
      URL.revokeObjectURL(cropImageSrc);
    }
    setCropImageSrc(null);
    setIsCropDialogOpen(false);
  };

  const handleCropSave = async () => {
    if (!cropImageSrc || !croppedAreaPixels) {
      handleCloseCropDialog();
      return;
    }

    try {
      const croppedFile = await getCroppedFile(cropImageSrc, croppedAreaPixels, cropFileName);
      hook.methods.setValue('image', croppedFile, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } catch (error) {
      console.error(error);
    } finally {
      handleCloseCropDialog();
    }
  };

  useEffect(() => {
    if (hook.formState.submitCount === 0) {
      return;
    }

    const errorKeys = new Set(Object.keys(hook.formState.errors || {}));

    const displayFields = [
      'exhibitionEnv',
      'businessUnitId',
      'lpEvent',
      'benefitForNewUsers',
      'newUsersPeriod',
      'private',
      'dontShowToImported',
      'blocking',
      'test',
      'bonusProduct',
      'exclusiveToBiome',
      'onlyToRedeemedPoints',
      'isGift',
      'highlightCategory',
      'localType',
      'company',
      'local',
      'show',
      'showTo',
      'tiers',
      'priority',
    ];

    const infoFields = [
      'name',
      'price',
      'discountPercentage',
      'startDate',
      'endDate',
      'image',
      'description',
      'secondaryDescription',
      'details',
      'category',
      'theme',
      'rules',
    ];

    const redeemFields = [
      'rewardType',
      'codeShowType',
      'redeemCodeQuantity',
      'expirationDays',
      'userPurchaseLimit',
      'totalQuantity',
      'uniqueCode',
      'hasQuantity',
      'location',
    ];

    const feedbackFields = ['hasCustomerVoice', 'customerVoiceData'];

    setExpandedSections((current) => {
      const next = { ...current };
      let hasChanges = false;

      if (displayFields.some((field) => errorKeys.has(field)) && !next.display) {
        next.display = true;
        hasChanges = true;
      }

      if (infoFields.some((field) => errorKeys.has(field)) && !next.info) {
        next.info = true;
        hasChanges = true;
      }

      if (redeemFields.some((field) => errorKeys.has(field)) && !next.redeem) {
        next.redeem = true;
        hasChanges = true;
      }

      if (feedbackFields.some((field) => errorKeys.has(field)) && !next.feedback) {
        next.feedback = true;
        hasChanges = true;
      }

      return hasChanges ? next : current;
    });
  }, [hook.formState.errors, hook.formState.submitCount]);

  useEffect(() => {
    const updateActionBarFrame = () => {
      if (!actionBarAnchorRef.current || !stickyActionBarRef.current) {
        return;
      }

      const anchorRect = actionBarAnchorRef.current.getBoundingClientRect();
      const shouldBePinned = anchorRect.top <= stickyTop;

      const nextFrame = {
        left: Math.round(anchorRect.left),
        width: Math.round(anchorRect.width),
        height: Math.round(stickyActionBarRef.current.getBoundingClientRect().height),
      };

      setIsActionBarPinned((current) => (current === shouldBePinned ? current : shouldBePinned));
      setActionBarFrame((current) =>
        current.left === nextFrame.left &&
        current.width === nextFrame.width &&
        current.height === nextFrame.height
          ? current
          : nextFrame
      );
    };

    updateActionBarFrame();

    window.addEventListener('scroll', updateActionBarFrame, { passive: true });
    window.addEventListener('resize', updateActionBarFrame);

    return () => {
      window.removeEventListener('scroll', updateActionBarFrame);
      window.removeEventListener('resize', updateActionBarFrame);
    };
  }, [stickyTop]);

  useEffect(() => {
    const updateSubmitActionFrame = () => {
      if (!submitActionAnchorRef.current || !submitActionBarRef.current) {
        return;
      }

      const anchorRect = submitActionAnchorRef.current.getBoundingClientRect();
      const nextFrame = {
        left: Math.round(anchorRect.left),
        width: Math.round(anchorRect.width),
        height: Math.round(submitActionBarRef.current.getBoundingClientRect().height),
      };

      const shouldBePinned = anchorRect.bottom > window.innerHeight - submitFloatingBottom;

      setIsSubmitActionPinned((current) => (current === shouldBePinned ? current : shouldBePinned));
      setSubmitActionFrame((current) =>
        current.left === nextFrame.left &&
        current.width === nextFrame.width &&
        current.height === nextFrame.height
          ? current
          : nextFrame
      );
    };

    updateSubmitActionFrame();

    window.addEventListener('scroll', updateSubmitActionFrame, { passive: true });
    window.addEventListener('resize', updateSubmitActionFrame);

    return () => {
      window.removeEventListener('scroll', updateSubmitActionFrame);
      window.removeEventListener('resize', updateSubmitActionFrame);
    };
  }, [submitFloatingBottom]);

  useEffect(() => {
    if (isCoupon) {
      if (!['code', 'giftback'].includes(rewardType)) {
        hook.methods.setValue('rewardType', 'code', { shouldDirty: true, shouldValidate: true });
      }

      return;
    }

    if (isWhatsAppEnv) {
      if (!['simple', 'uniqueCode'].includes(rewardType)) {
        hook.methods.setValue('rewardType', 'simple', { shouldDirty: true, shouldValidate: true });
      }

      return;
    }

    if (rewardType !== 'simple') {
      hook.methods.setValue('rewardType', 'simple', { shouldDirty: true, shouldValidate: true });
    }
  }, [hook.methods, isCoupon, isWhatsAppEnv, rewardType]);

  useEffect(() => {
    if (previousLocalType.current === localType) {
      return;
    }

    hook.methods.setValue('company', null, { shouldValidate: true });
    hook.methods.setValue('local', null, { shouldValidate: true });
    previousLocalType.current = localType;
  }, [hook.methods, localType]);

  useEffect(() => {
    if (previousBenefitType.current === benefitType) {
      return;
    }

    if (isVivaEnv) {
      const sourceValues = previousBenefitType.current === 'coupon' ? showTo : show;

      const nextFieldName = benefitType === 'coupon' ? 'showTo' : 'show';

      hook.methods.setValue(nextFieldName, Array.isArray(sourceValues) ? sourceValues : [], {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    previousBenefitType.current = benefitType;
  }, [hook.methods, isVivaEnv, benefitType, show, showTo]);

  const renderSelectableSingleChipField = (
    name: 'category' | 'theme',
    label: string,
    options: { value: string; label: string }[]
  ) => (
    <Controller
      name={name}
      control={hook.control}
      render={({ field, fieldState: { error } }) => {
        const currentValue = typeof field.value === 'string' ? field.value : '';

        return (
          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              {label}
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {options.map((option) => {
                const isSelected = currentValue === option.value;

                return (
                  <Chip
                    key={option.value}
                    label={option.label}
                    clickable
                    onClick={() => field.onChange(isSelected ? '' : option.value)}
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: 600,
                      color: isSelected ? 'common.white' : 'text.primary',
                      '& .MuiChip-label': {
                        color: isSelected ? 'common.white' : 'inherit',
                      },
                      '&:hover': {
                        backgroundColor: isSelected ? 'primary.main' : 'action.hover',
                        color: isSelected ? 'common.white' : 'text.primary',
                      },
                    }}
                  />
                );
              })}
            </Stack>
            {error ? (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {error.message}
              </Typography>
            ) : null}
          </Stack>
        );
      }}
    />
  );

  const renderSelectableMultiChipField = (
    name: 'showTo' | 'show' | 'tiers',
    label: string,
    options: { value: string; label: string }[],
    onOptionClick?: (clickedValue: string, selectedValues: string[]) => string[]
  ) => (
    <Controller
      key={name}
      name={name}
      control={hook.control}
      render={({ field, fieldState: { error } }) => {
        const selectedValues = Array.isArray(field.value) ? field.value : [];

        return (
          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              {label}
            </Typography>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {options.map((option) => {
                const isSelected = selectedValues.includes(option.value);

                return (
                  <Chip
                    key={option.value}
                    label={option.label}
                    clickable
                    onClick={() => {
                      if (onOptionClick) {
                        field.onChange(onOptionClick(option.value, selectedValues));
                        return;
                      }

                      field.onChange(
                        isSelected
                          ? selectedValues.filter((value) => value !== option.value)
                          : [...selectedValues, option.value]
                      );
                    }}
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: 600,
                      color: isSelected ? 'common.white' : 'text.primary',
                      '& .MuiChip-label': {
                        color: isSelected ? 'common.white' : 'inherit',
                      },
                      '&:hover': {
                        backgroundColor: isSelected ? 'primary.main' : 'action.hover',
                        color: isSelected ? 'common.white' : 'text.primary',
                      },
                    }}
                  />
                );
              })}
            </Stack>

            {error ? (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {error.message}
              </Typography>
            ) : null}
          </Stack>
        );
      }}
    />
  );

  const renderBooleanToggle = (name: any, label: string) => (
    <Controller
      name={name}
      control={hook.control}
      render={({ field }) => (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.300',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            backgroundColor: theme.palette.grey[100],
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {label}
          </Typography>
          <Switch
            checked={Boolean(field.value)}
            onChange={(_, checked) => field.onChange(checked)}
          />
        </Box>
      )}
    />
  );

  const renderConditionalReveal = (condition: boolean, content: ReactNode) => (
    <Collapse in={condition} timeout={{ enter: 280, exit: 200 }} unmountOnExit>
      <Box
        sx={{
          pt: 0.5,
          opacity: condition ? 1 : 0,
          transform: condition ? 'translateY(0)' : 'translateY(-4px)',
          transition: 'opacity 220ms ease, transform 220ms ease',
        }}
      >
        {content}
      </Box>
    </Collapse>
  );

  const renderDisplayFieldBox = (content: ReactNode) => (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'grey.300',
        backgroundColor: theme.palette.grey[100],
      }}
    >
      {content}
    </Box>
  );

  const renderDisplayTypeField = () =>
    renderDisplayFieldBox(
      <Stack spacing={2}>
        <Typography variant="body2" fontWeight={600}>
          Tipo de exibição
        </Typography>
        <RHFRadioGroupLegacy
          name="private"
          options={[...hook.PRIVATE_OPTIONS]}
          getOptionLabel={hook.PRIVATE_LABELS}
        />

        {renderConditionalReveal(
          isPrivate,
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.300',
              backgroundColor: theme.palette.grey[100],
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                <Button variant="soft" onClick={() => hook.setOpenCpfs(true)}>
                  Cadastrar Cpfs
                </Button>
                {hasRegisteredCpfs && (
                  <Button
                    variant="soft"
                    startIcon={<Iconify icon="solar:download-linear" width={18} />}
                    onClick={hook.handleDownloadCpfs}
                  >
                    Baixar Planilha
                  </Button>
                )}
                <Typography variant="overline">
                  {hook.cpfs?.length || 0} Cpfs Cadastrados
                </Typography>
              </Stack>

              <Stack>
                <Typography variant="body1" fontWeight="bold">
                  Não mostrar para:
                </Typography>
                <RHFRadioGroupLegacy
                  name="dontShowToImported"
                  options={[...hook.DONT_OPTIONS]}
                  getOptionLabel={hook.DONT_LABELS}
                />
              </Stack>
            </Stack>
          </Box>
        )}
      </Stack>
    );

  const renderExhibitionEnvField = () => (
    <Box sx={{ gridColumn: '1 / -1' }}>
      <RHFSelect
        name="exhibitionEnv"
        label="Ambiente de exibição"
        variant="outlined"
        disabled={hook.isEdit}
      >
        {hook.EXHIBITION_ENV_OPTIONS.filter(
          (option: { value: string; label: string }) => option.value !== 'parking' || isCoupon
        ).map((option: { value: string; label: string }) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </RHFSelect>
    </Box>
  );

  const renderNewUsersField = ({ fullWidth = true }: { fullWidth?: boolean } = {}) => (
    <Controller
      name="benefitForNewUsers"
      control={hook.control}
      render={({ field }) => (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.300',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            backgroundColor: theme.palette.grey[100],
            ...(fullWidth && {
              gridColumn: '1 / -1',
            }),
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography variant="body2" fontWeight={600}>
              Benefício para novos usuários
            </Typography>
            <Switch
              checked={Boolean(field.value)}
              onChange={(_, checked) => field.onChange(checked)}
            />
          </Stack>

          {renderConditionalReveal(
            showNewUsersPeriod,
            <Stack spacing={1.5}>
              <Typography variant="body2" fontWeight={700}>
                Período de cadastro:
              </Typography>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Controller
                  name="newUsersPeriod.startDate"
                  control={hook.control}
                  render={({ field: periodField }) => (
                    <DatePicker
                      label="Data de Início"
                      value={periodField.value}
                      onChange={(value) => periodField.onChange(value)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  )}
                />

                <Controller
                  name="newUsersPeriod.endDate"
                  control={hook.control}
                  render={({ field: periodField }) => (
                    <DatePicker
                      label="Data de Fim"
                      value={periodField.value}
                      onChange={(value) => periodField.onChange(value)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  )}
                />
              </Stack>

              {!isValidNewUsersPeriod && (
                <Typography variant="caption" color="error">
                  Data de término deve ser maior ou igual à data de início
                </Typography>
              )}
            </Stack>
          )}
        </Box>
      )}
    />
  );

  const renderDisplayCard = () => {
    const locationOptions = hook.LOCATIONS_OPTIONS.filter((option) => option.value !== 'all');

    return (
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <SectionTitle index={1} title="Exibição" />
            <IconButton onClick={() => toggleSection('display')}>
              {expandedSections.display ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Stack>

          <Collapse in={expandedSections.display} timeout="auto" unmountOnExit={false}>
            <Stack spacing={3} sx={{ pt: 0.5 }}>
              {isParkingEnv && (
                <>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    {renderExhibitionEnvField()}
                  </Box>

                  <Divider />

                  <Stack spacing={2}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Visibilidade do benefício
                    </Typography>

                    {renderSelectableMultiChipField(
                      'tiers',
                      'Tier dos usuários',
                      hook.TIER_OPTIONS.filter(
                        (option: { value: string; label: string }) => option.value !== 'all'
                      ),
                      (clickedValue, selectedValues) =>
                        selectedValues.includes(clickedValue) ? [] : [clickedValue]
                    )}

                    {renderStepperNumberField('priority', 'Nível de prioridade', 0)}
                  </Stack>
                </>
              )}
              {!isParkingEnv && isRewardEnv && (
                <>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    {renderExhibitionEnvField()}
                  </Box>

                  <Divider />

                  <Stack spacing={2}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Configuração de Local
                    </Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={700}>
                          Tipo de Local:
                        </Typography>
                        <RHFRadioGroupLegacy
                          name="localType"
                          options={[...hook.LOCAL_TYPE_OPTIONS]}
                          getOptionLabel={hook.LOCAL_TYPE_LABELS}
                        />

                        {renderConditionalReveal(
                          isLocalTypeCompany,
                          <RHFAutocomplete
                            name="company"
                            label="Empresa"
                            options={hook.COMPANY_OPTIONS}
                          />
                        )}
                        {renderConditionalReveal(
                          !isLocalTypeCompany,
                          <RHFAutocomplete name="local" label="Local" options={locationOptions} />
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </>
              )}
              {!isParkingEnv && !isRewardEnv && (
                <>
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Regras de Visibilidade
                    </Typography>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 2,
                      }}
                    >
                      {renderExhibitionEnvField()}

                      {isLpEventsEnv && (
                        <>
                          <Box sx={{ gridColumn: '1 / -1' }}>
                            <RHFSelect
                              name="businessUnitId"
                              label="Unidade de Negócio"
                              variant="outlined"
                            >
                              {(hook.BUSINESS_UNIT_OPTIONS || []).map((option: any) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </RHFSelect>
                          </Box>

                          <Box sx={{ gridColumn: '1 / -1' }}>
                            <RHFSelect name="lpEvent" label="Evento LP" variant="outlined">
                              {(hook.LP_EVENTS_OPTIONS || []).map((option: any) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </RHFSelect>
                          </Box>
                        </>
                      )}

                      {isCoupon && isVivaEnv && renderNewUsersField()}

                      {isCoupon && isVivaEnv && (
                        <Box sx={{ gridColumn: '1 / -1' }}>{renderDisplayTypeField()}</Box>
                      )}

                      {!isCoupon && isVivaEnv && renderBooleanToggle('isGift', 'Vale presente')}

                      {isVivaEnv && (
                        <Controller
                          name="blocking"
                          control={hook.control}
                          render={({ field }) => (
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.300',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                backgroundColor: theme.palette.grey[100],
                              }}
                            >
                              <Typography variant="body2" fontWeight={600}>
                                Bloqueio em 30 dias
                              </Typography>
                              <Switch
                                checked={field.value === 'all'}
                                onChange={(_, checked) => field.onChange(checked ? 'all' : 'self')}
                              />
                            </Box>
                          )}
                        />
                      )}
                      {isVivaEnv && renderBooleanToggle('test', 'Benefício para teste')}
                      {!isCoupon &&
                        isVivaEnv &&
                        renderBooleanToggle('bonusProduct', 'Produto bonificado')}
                      {isVivaEnv &&
                        renderBooleanToggle(
                          'exclusiveToBiome',
                          'Exclusivo para usuários VIVA Bioma'
                        )}

                      {isWhatsAppEnv && (
                        <Box sx={{ gridColumn: '1 / -1' }}>
                          {renderBooleanToggle(
                            'onlyToRedeemedPoints',
                            'Exibir somente para usuários que resgataram pontos'
                          )}
                        </Box>
                      )}

                      {!isCoupon && isVivaEnv && renderNewUsersField({ fullWidth: false })}

                      {!isCoupon && isVivaEnv && (
                        <Box sx={{ gridColumn: '1 / -1' }}>{renderDisplayTypeField()}</Box>
                      )}
                    </Box>
                  </Stack>

                  <Divider />

                  {isLpEventsEnv && (
                    <>
                      <Stack spacing={2}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          Destaque
                        </Typography>

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                          <Controller
                            name="highlightCategory"
                            control={hook.control}
                            render={({ field }) => (
                              <Box
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  border: '1px solid',
                                  borderColor: 'grey.300',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: 2,
                                  backgroundColor: theme.palette.grey[100],
                                }}
                              >
                                <Typography variant="body2" fontWeight={600}>
                                  Destaque Home Eventos LP
                                </Typography>
                                <Switch
                                  checked={isLpEventsHomeHighlighted}
                                  onChange={(_, checked) => {
                                    field.onChange(
                                      checked ? ['lp_events_home_highlights'] : ['without']
                                    );
                                  }}
                                />
                              </Box>
                            )}
                          />
                        </Box>
                      </Stack>

                      <Divider />
                    </>
                  )}

                  {!isWhatsAppEnv && (
                    <>
                      <Stack spacing={2}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          Configuração de Local
                        </Typography>

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                          <Stack spacing={1}>
                            <Typography variant="body1" fontWeight={700}>
                              Tipo de Local:
                            </Typography>
                            <RHFRadioGroupLegacy
                              name="localType"
                              options={[...hook.LOCAL_TYPE_OPTIONS]}
                              getOptionLabel={hook.LOCAL_TYPE_LABELS}
                            />

                            {renderConditionalReveal(
                              isLocalTypeCompany,
                              <RHFAutocomplete
                                name="company"
                                label="Empresa"
                                options={hook.COMPANY_OPTIONS}
                              />
                            )}
                            {renderConditionalReveal(
                              !isLocalTypeCompany,
                              <RHFAutocomplete
                                name="local"
                                label="Local"
                                options={locationOptions}
                              />
                            )}
                          </Stack>
                        </Box>
                      </Stack>

                      <Divider />
                    </>
                  )}

                  {!isWhatsAppEnv && (
                    <Stack spacing={2}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Visibilidade do benefício
                      </Typography>

                      {isVivaEnv &&
                        renderSelectableMultiChipField(
                          isCoupon ? 'showTo' : 'show',
                          'Exibir para usuários que escanearam notas nesses locais',
                          hook.showToLocations
                        )}

                      {isVivaEnv &&
                        renderSelectableMultiChipField(
                          'tiers',
                          'Tiers dos usuários',
                          hook.TIER_OPTIONS,
                          (clickedValue, selectedValues) => {
                            const isSelected = selectedValues.includes(clickedValue);

                            if (clickedValue === 'all') {
                              return isSelected
                                ? selectedValues.filter((value) => value !== 'all')
                                : ['all'];
                            }

                            if (isSelected) {
                              if (selectedValues.length === 1) {
                                return ['all'];
                              }
                              return selectedValues.filter((value) => value !== clickedValue);
                            }

                            if (selectedValues.length === 2) {
                              return ['all'];
                            }

                            return [
                              ...selectedValues.filter((value) => value !== 'all'),
                              clickedValue,
                            ];
                          }
                        )}

                      {(!isCoupon || !isWhatsAppEnv) &&
                        renderStepperNumberField('priority', 'Nível de prioridade', 0)}
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  const renderDetailsField = () => (
    <Stack spacing={1}>
      <Typography variant="subtitle1" fontWeight={600}>
        Detalhes
      </Typography>

      {hook.fields.map((field, index) => (
        <Grid key={field.id} container spacing={1} alignItems="flex-start">
          <Grid item xs={12} md={11}>
            <RHFTextField name={`details.${index}.value`} label={`Detalhe ${index + 1}`} />
          </Grid>
          <Grid
            item
            xs={12}
            md={1}
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-end', md: 'center' },
              alignItems: 'center',
              height: { xs: 'auto', md: 56 },
              pt: 0,
            }}
          >
            <IconButton
              onClick={() => {
                if (hook.fields.length > 1) {
                  hook.removeDetail(index);
                }
              }}
            >
              <ClearIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}

      <Button variant="soft" onClick={() => hook.appendDetail()} sx={{ alignSelf: 'flex-start' }}>
        + Adicionar Detalhe
      </Button>
    </Stack>
  );

  const renderSharedInformationFields = () => (
    <>
      <RHFTextField
        name="name"
        label="Nome do Benefício"
        helperText={
          benefitType === 'coupon' && !isParkingEnv ? (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Iconify icon="eva:info-outline" width={16} />
              <Typography variant="caption">
                Nome do benefício é somente para fins de relatório interno.
              </Typography>
            </Stack>
          ) : undefined
        }
        InputProps={{
          endAdornment: renderRedeemCopyIcon(selectedRedeemName, () =>
            setFieldFromRedeem('name', selectedRedeemName)
          ),
        }}
      />
      <Grid container spacing={2}>
        {(isVivaEnv || isWhatsAppEnv || isRewardEnv || isParkingEnv) && (
          <Grid item xs={12} md={isParkingEnv ? 3 : 4}>
            <RHFTextField
              name="price"
              label="Valor (em pontos)"
              type="number"
              disabled={isRewardCoupon}
              InputProps={{
                endAdornment: isRewardCoupon
                  ? null
                  : renderRedeemCopyIcon(hook.currentRedeem?.details?.price, () =>
                      setFieldFromRedeem('price', Number(hook.currentRedeem?.details?.price) || 0)
                    ),
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const next = Number(event.target.value);

                if (event.target.value === '') {
                  hook.methods.setValue('price', 0, { shouldValidate: true });
                  return;
                }

                if (Number.isNaN(next)) {
                  return;
                }

                hook.methods.setValue(
                  'price',
                  Math.min(MAX_NUMERIC_FIELD_VALUE, Math.max(0, next)),
                  {
                    shouldValidate: true,
                  }
                );
              }}
              inputProps={{
                min: 0,
                max: MAX_NUMERIC_FIELD_VALUE,
                step: 1,
                onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
                  if (BLOCKED_INTEGER_INPUT_KEYS.includes(event.key)) {
                    event.preventDefault();
                  }
                },
              }}
            />
          </Grid>
        )}
        {isParkingEnv && (
          <Grid item xs={12} md={3}>
            <Controller
              name="discountPercentage"
              control={hook.control}
              render={({ field, fieldState: { error } }) => (
                <NumericFormat
                  customInput={TextField}
                  fullWidth
                  label="Porcentagem de desconto"
                  value={field.value ?? ''}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  suffix="%"
                  allowNegative={false}
                  isAllowed={({ floatValue }) => floatValue === undefined || floatValue <= 100}
                  onValueChange={({ floatValue }) => field.onChange(floatValue ?? '')}
                  onBlur={field.onBlur}
                  name={field.name}
                  getInputRef={field.ref}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
        )}
        {!isRewardEnv && (
          <>
            <Grid item xs={12} md={informationDateColumnSize}>
              <Controller
                name="startDate"
                control={hook.control}
                render={({ field }) => (
                  <DatePicker
                    label="Data de Início"
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={informationDateColumnSize}>
              <Controller
                name="endDate"
                control={hook.control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    label="Data de Fim"
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </>
        )}
      </Grid>

      {!isParkingEnv && (
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              spacing={1}
            >
              <Typography variant="subtitle1">Imagem</Typography>
              {imageValue && (
                <Button
                  sx={{
                    gap: 1,
                  }}
                  onClick={handleDownloadImage}
                >
                  <Iconify icon="lucide:download" />
                  Baixar Imagem
                </Button>
              )}
            </Stack>
            {renderRedeemCopyIcon(selectedRedeemImage, () =>
              setFieldFromRedeem('image', selectedRedeemImage)
            )}
          </Stack>
          <RHFUploadSingleFileAccelerator
            name="image"
            acceptAll={false}
            // @ts-ignore
            onDrop={handleImageDrop}
          />
        </Stack>
      )}
      {!isWhatsAppEnv && !isParkingEnv && (
        <RHFTextField
          name="description"
          label="Título"
          multiline
          rows={4}
          InputProps={{
            endAdornment: renderRedeemCopyIcon(hook.currentRedeem?.details?.description, () =>
              setFieldFromRedeem('description', hook.currentRedeem?.details?.description || '')
            ),
          }}
        />
      )}
      {isCoupon && isVivaEnv && (
        <RHFTextField name="secondaryDescription" label="Descrição" multiline rows={3} />
      )}
      {!isCoupon && isVivaEnv && renderDetailsField()}
      {!isWhatsAppEnv &&
        !isParkingEnv &&
        renderSelectableSingleChipField('category', 'Categoria', hook.CATEGORY_OPTIONS)}
      {!isWhatsAppEnv &&
        !isRewardEnv &&
        !isParkingEnv &&
        renderSelectableSingleChipField('theme', 'Tema', hook.THEME_OPTIONS)}
      <Box
        sx={{
          '& .ql-editor': {
            maxHeight: 200,
            overflowY: 'auto',
          },
        }}
      >
        <RHFEditor name="rules" label="Regras " />
      </Box>
    </>
  );

  const renderCouponInformationForm = () => (
    <Stack spacing={3}>{renderSharedInformationFields()}</Stack>
  );

  const renderProductInformationForm = () => (
    <Stack spacing={3}>{renderSharedInformationFields()}</Stack>
  );

  const renderStepperNumberField = (
    name: keyof Pick<
      BenefitDetailsFormValues,
      'redeemCodeQuantity' | 'expirationDays' | 'userPurchaseLimit' | 'totalQuantity' | 'priority'
    >,
    label: string,
    minValue = 0,
    maxValue = MAX_NUMERIC_FIELD_VALUE,
    copyAction?: {
      value: unknown;
      onClick: () => void;
    }
  ) => (
    <Controller
      name={name}
      control={hook.control}
      render={({ field, fieldState: { error } }) => {
        const parsed = Number(field.value);
        const hasValue = field.value !== null && field.value !== undefined;
        const currentValue = Number.isFinite(parsed)
          ? Math.min(maxValue, Math.max(minValue, parsed))
          : minValue;
        const displayValue = hasValue ? String(field.value) : '';
        const priorityHelperText =
          name === 'priority' ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Iconify icon="eva:info-outline" sx={{ width: 16, height: 16 }} />
              <Typography variant="caption">
                Prioridade por ordem crescente: quanto menor o numero, mais alta a prioridade do
                beneficio.
              </Typography>
            </Stack>
          ) : undefined;

        const updateValue = (nextValue: number) => {
          field.onChange(Math.min(maxValue, Math.max(minValue, nextValue)));
        };

        return (
          <TextField
            fullWidth
            type="number"
            label={label}
            value={displayValue}
            onChange={(event) => {
              if (event.target.value === '') {
                field.onChange('');
                return;
              }

              const next = Number(event.target.value);
              if (Number.isNaN(next)) return;
              field.onChange(Math.min(maxValue, Math.max(minValue, next)));
            }}
            onBlur={() => {
              const next = Number(field.value);
              if (!Number.isFinite(next)) {
                updateValue(minValue);
                return;
              }

              updateValue(next);
            }}
            onFocus={(event) => {
              event.target.select();
            }}
            error={!!error}
            helperText={error?.message || priorityHelperText}
            inputProps={{
              min: minValue,
              max: maxValue,
              step: 1,
              onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
                if (BLOCKED_INTEGER_INPUT_KEYS.includes(event.key)) {
                  event.preventDefault();
                }
              },
              onWheel: (event: WheelEvent<HTMLInputElement>) => {
                event.currentTarget.blur();
              },
            }}
            sx={{
              '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ mr: -0.5 }}>
                  <Stack direction="row" spacing={0.4} alignItems="center">
                    {copyAction ? renderRedeemCopyIcon(copyAction.value, copyAction.onClick) : null}
                    <Stack spacing={0.2}>
                      <IconButton
                        size="small"
                        onClick={() => updateValue(currentValue + 1)}
                        sx={{ p: 0.2 }}
                      >
                        <KeyboardArrowUpIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => updateValue(currentValue - 1)}
                        sx={{ p: 0.2 }}
                      >
                        <KeyboardArrowDownIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </InputAdornment>
              ),
            }}
          />
        );
      }}
    />
  );

  const renderRedeemCard = () => (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <SectionTitle index={3} title="Resgate" />
          <IconButton onClick={() => toggleSection('redeem')}>
            {expandedSections.redeem ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Stack>

        <Collapse in={expandedSections.redeem} timeout="auto" unmountOnExit={false}>
          <Stack spacing={3} sx={{ pt: 0.5 }}>
            {isWhatsAppEnv && (
              <Stack spacing={2}>
                {renderConditionalReveal(
                  isWhatsAppProduct,
                  <Stack spacing={1}>
                    <Typography variant="body1" fontWeight={700}>
                      Tipo de resgate:
                    </Typography>
                    <RHFRadioGroupLegacy
                      name="rewardType"
                      options={[...hook.PRODUCT_WPP_REWARD_OPTIONS]}
                      getOptionLabel={hook.PRODUCT_WPP_REWARD_LABELS}
                    />
                  </Stack>
                )}

                {renderConditionalReveal(
                  isCoupon || rewardType === 'uniqueCode',
                  <RHFTextField name="uniqueCode" label="Código Único" />
                )}

                <Controller
                  name="hasQuantity"
                  control={hook.control}
                  render={({ field }) => (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        backgroundColor: theme.palette.grey[100],
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          Possui estoque
                        </Typography>
                        <Switch
                          checked={Boolean(field.value)}
                          onChange={(_, checked) => field.onChange(checked)}
                        />
                      </Stack>

                      {renderConditionalReveal(
                        Boolean(field.value),
                        <RHFTextField
                          type="number"
                          name="totalQuantity"
                          label="Quantidade/Estoque"
                          InputProps={{
                            endAdornment: renderRedeemCopyIcon(
                              hook.currentRedeem?.stockManager?.quantity,
                              () =>
                                setFieldFromRedeem(
                                  'totalQuantity',
                                  Number(hook.currentRedeem?.stockManager?.quantity) || 0
                                )
                            ),
                          }}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const next = Number(event.target.value);

                            if (event.target.value === '') {
                              hook.methods.setValue('totalQuantity', 0, { shouldValidate: true });
                              return;
                            }

                            if (Number.isNaN(next)) {
                              return;
                            }

                            hook.methods.setValue(
                              'totalQuantity',
                              Math.min(MAX_NUMERIC_FIELD_VALUE, Math.max(0, next)),
                              { shouldValidate: true }
                            );
                          }}
                          inputProps={{
                            min: 0,
                            max: MAX_NUMERIC_FIELD_VALUE,
                            step: 1,
                            onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
                              if (BLOCKED_INTEGER_INPUT_KEYS.includes(event.key)) {
                                event.preventDefault();
                              }
                            },
                          }}
                        />
                      )}
                    </Box>
                  )}
                />
              </Stack>
            )}

            {isCoupon && !isWhatsAppEnv && (
              <Stack spacing={2}>
                {isVivaEnv && (
                  <Stack spacing={1}>
                    <Typography variant="body1" fontWeight={700}>
                      Tipo de resgate:
                    </Typography>
                    <Controller
                      name="rewardType"
                      control={hook.control}
                      render={({ field }) => (
                        <RadioGroup row value={field.value} onChange={field.onChange}>
                          <FormControlLabel value="code" control={<Radio />} label="Código/Link" />
                          <FormControlLabel
                            value="giftback"
                            control={<Radio />}
                            label="Giftback"
                            disabled
                          />
                        </RadioGroup>
                      )}
                    />
                  </Stack>
                )}

                <Stack spacing={1}>
                  <Typography variant="body1" fontWeight={700}>
                    Tipo de exibição dos codigos/links:
                  </Typography>
                  <RHFRadioGroupLegacy
                    name="codeShowType"
                    options={[...hook.CODE_SHOW_OPTIONS]}
                    getOptionLabel={hook.CODE_SHOW_LABELS}
                  />
                </Stack>

                <Stack
                  direction="row"
                  sx={{
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                  }}
                  spacing={2}
                >
                  <Button variant="soft" onClick={() => hook.setOpen(true)}>
                    Cadastrar Codigos/Links
                  </Button>
                  {hasRegisteredCodes && (
                    <Button
                      variant="soft"
                      startIcon={<Iconify icon="solar:download-linear" width={18} />}
                      onClick={hook.handleDownloadCodes}
                    >
                      Baixar Planilha
                    </Button>
                  )}
                  <Typography variant="overline">
                    {hook?.codesEdited
                      ? `${hook?.codes?.length || 0} Codigos/Links cadastrados`
                      : `${(hook?.codes?.length || 0) * Number(redeemCodeQuantity || 1)} Codigos/Links cadastrados`}
                  </Typography>
                </Stack>

                <>
                  <Divider />

                  <Stack spacing={1.5}>
                    <Typography variant="h6" fontWeight={700}>
                      Limites
                    </Typography>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      {isVivaEnv ? (
                        <Stack spacing={0.6} sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={limitCaptionSx}>
                            Quantidade de códigos/links por resgate
                          </Typography>
                          {renderStepperNumberField('redeemCodeQuantity', '', 1)}
                        </Stack>
                      ) : null}

                      <Stack spacing={0.6} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={limitCaptionSx}>
                          Tempo de retirada após o resgate (em dias)
                        </Typography>
                        {renderStepperNumberField('expirationDays', '', 0)}
                      </Stack>

                      {!isRewardEnv && (
                        <Stack spacing={0.6} sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={limitCaptionSx}>
                            Quantidade máxima de resgates por usuário
                          </Typography>
                          {renderStepperNumberField('userPurchaseLimit', '', 1)}
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                </>
              </Stack>
            )}

            {!isCoupon && !isWhatsAppEnv && (
              <>
                <Stack spacing={1}>
                  <Typography variant="body1" fontWeight={700}>
                    Local de retirada:
                  </Typography>
                  <RHFTextField name="location" label="Local de retirada" />
                </Stack>

                <Divider />

                <Stack spacing={1.5}>
                  <Typography variant="h6" fontWeight={700}>
                    Limites
                  </Typography>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Stack spacing={0.6} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={limitCaptionSx}>
                        Quantidade/Estoque
                      </Typography>
                      {renderStepperNumberField('totalQuantity', '', 0, MAX_NUMERIC_FIELD_VALUE, {
                        value: hook.currentRedeem?.stockManager?.quantity,
                        onClick: () =>
                          setFieldFromRedeem(
                            'totalQuantity',
                            Number(hook.currentRedeem?.stockManager?.quantity) || 0
                          ),
                      })}
                    </Stack>

                    <Stack spacing={0.6} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={limitCaptionSx}>
                        Tempo de retirada após resgate (em dias)
                      </Typography>
                      {renderStepperNumberField('expirationDays', '', 0)}
                    </Stack>

                    {!isRewardEnv && (
                      <Stack spacing={0.6} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={limitCaptionSx}>
                          Quantidade máxima de resgates por usuário
                        </Typography>
                        {renderStepperNumberField('userPurchaseLimit', '', 1)}
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );

  const renderFeedbackCard = () => (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <SectionTitle index={4} title="Formulário de Feedback" />
          <IconButton onClick={() => toggleSection('feedback')}>
            {expandedSections.feedback ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Stack>

        <Collapse in={expandedSections.feedback} timeout="auto" unmountOnExit={false}>
          <Stack spacing={3} sx={{ pt: 0.5 }}>
            {isFeedbackFormAvailableEnv && (
              <Controller
                name="hasCustomerVoice"
                control={hook.control}
                render={({ field }) => (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'grey.300',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      backgroundColor: theme.palette.grey[100],
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={2}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        Customer Voice
                      </Typography>
                      <Switch
                        checked={Boolean(field.value)}
                        onChange={(_, checked) => {
                          field.onChange(checked);

                          if (checked) {
                            return;
                          }

                          hook.methods.setValue('customerVoiceData.baseUrl', '', {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          hook.methods.setValue('customerVoiceData.assetsUrl', '', {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          hook.methods.setValue('customerVoiceData.customerVoiceFormId', '', {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }}
                      />
                    </Stack>

                    {renderConditionalReveal(
                      hasCustomerVoice,
                      <Stack spacing={2}>
                        <Stack spacing={2}>
                          <RHFTextField name="customerVoiceData.baseUrl" label="URL base" />
                          <RHFTextField
                            name="customerVoiceData.assetsUrl"
                            label="URL dos recursos"
                          />
                          <RHFTextField
                            name="customerVoiceData.customerVoiceFormId"
                            label="ID do formulário do Customer Voice"
                          />
                        </Stack>
                        <Stack direction="row" justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            disableRipple
                            disableFocusRipple
                            disableTouchRipple
                            onClick={() => setIsCustomerVoiceScriptDialogOpen(true)}
                          >
                            Inserir script
                          </Button>
                        </Stack>
                      </Stack>
                    )}
                  </Box>
                )}
              />
            )}

            {!isFeedbackFormAvailableEnv && (
              <Typography variant="body2" color="text.secondary">
                Formulário de Feedback está disponível apenas para os ambientes VIVA, Eventos LP e
                Trilha.
              </Typography>
            )}

            <Dialog
              open={isCustomerVoiceScriptDialogOpen}
              onClose={() => setIsCustomerVoiceScriptDialogOpen(false)}
              fullWidth
              maxWidth="md"
            >
              <DialogTitle>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                  <Typography variant="h6">Colar script do Customer Voice</Typography>
                  <Stack spacing={1} alignItems="flex-start">
                    <Stack direction="row" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        Como conseguir o script
                      </Typography>
                      <Tooltip
                        arrow
                        placement="left"
                        title={
                          <Stack spacing={0.5} sx={{ py: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              Como obter o script (exemplo)
                            </Typography>
                            <Typography variant="caption">1. Acesse aba de inserção.</Typography>
                            <Typography variant="caption">2. Escolher formato embutido.</Typography>
                            <Typography variant="caption">
                              3. Copie o script dentro do campo de código inserido (abaixo você pode
                              ver uma imagem de exemplo).
                            </Typography>
                          </Stack>
                        }
                      >
                        <IconButton size="small" sx={{ p: 0.4 }}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Box
                      ref={scriptInstructionsPreviewAnchorRef}
                      component="img"
                      src="/assets/script_instructions.png"
                      alt="Exemplo de como localizar e copiar o script"
                      onMouseEnter={() => setIsScriptInstructionsPreviewOpen(true)}
                      onMouseLeave={() => setIsScriptInstructionsPreviewOpen(false)}
                      sx={{
                        width: 160,
                        maxWidth: '100%',
                        display: 'block',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'default',
                        boxShadow: 1,
                        bgcolor: 'background.paper',
                      }}
                    />
                  </Stack>
                </Stack>
              </DialogTitle>
              <DialogContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Script embedado
                </Typography>
                {typeof document !== 'undefined' && isScriptInstructionsPreviewOpen
                  ? createPortal(
                      <Box
                        onMouseEnter={() => setIsScriptInstructionsPreviewOpen(true)}
                        onMouseLeave={() => setIsScriptInstructionsPreviewOpen(false)}
                        sx={{
                          position: 'fixed',
                          inset: 0,
                          zIndex: (currentTheme) => currentTheme.zIndex.modal + 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pointerEvents: 'none',
                        }}
                      >
                        <Box
                          component="img"
                          src="/assets/script_instructions.png"
                          alt="Exemplo ampliado de como localizar e copiar o script"
                          sx={{
                            width: 'min(92vw, 1200px)',
                            maxHeight: '88vh',
                            objectFit: 'contain',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: (currentTheme) => currentTheme.shadows[24],
                            backgroundColor: 'background.paper',
                            pointerEvents: 'none',
                          }}
                        />
                      </Box>,
                      document.body
                    )
                  : null}
                <TextField
                  autoFocus
                  fullWidth
                  multiline
                  minRows={6}
                  value={customerVoiceEmbedScript}
                  onChange={(event) => {
                    const nextScript = event.target.value;
                    setCustomerVoiceEmbedScript(nextScript);

                    const hasAutoCompleted = applyCustomerVoiceEmbedScript(nextScript);

                    if (hasAutoCompleted) {
                      setIsCustomerVoiceScriptDialogOpen(false);
                    }
                  }}
                  placeholder="Cole aqui o script completo para extrair os dados automaticamente"
                  helperText="Quando os dados forem extraídos com sucesso, este diálogo será fechado automaticamente."
                />
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'flex-end' }}>
                <Button onClick={() => setIsCustomerVoiceScriptDialogOpen(false)}>Cancelar</Button>
              </DialogActions>
            </Dialog>

            <Dialog open={isCropDialogOpen} onClose={handleCloseCropDialog} fullWidth maxWidth="md">
              <DialogTitle>Ajustar imagem</DialogTitle>
              <DialogContent>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: { xs: 320, sm: 420 },
                    bgcolor: 'grey.900',
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  {cropImageSrc && (
                    <Cropper
                      image={cropImageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={cropAspect}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={(_, croppedArea) => setCroppedAreaPixels(croppedArea)}
                    />
                  )}
                </Box>
                <Stack spacing={1} sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Zoom
                  </Typography>
                  <Slider
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(_, value) => setZoom(Number(value))}
                  />
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseCropDialog}>Cancelar</Button>
                <Button variant="contained" onClick={handleCropSave}>
                  Aplicar
                </Button>
              </DialogActions>
            </Dialog>
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );

  return (
    <FormProvider methods={hook.methods} onSubmit={hook.onSubmit}>
      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} lg={8}>
          <Stack spacing={3}>
            <Box
              ref={actionBarAnchorRef}
              sx={{ minHeight: isActionBarPinned ? `${actionBarFrame.height}px` : 'auto' }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                spacing={2}
                ref={stickyActionBarRef}
                sx={{
                  position: isActionBarPinned ? 'fixed' : 'relative',
                  top: isActionBarPinned ? stickyTop : 'auto',
                  left: isActionBarPinned ? actionBarFrame.left : 'auto',
                  zIndex: (currentTheme) => currentTheme.zIndex.appBar - 1,
                  width: isActionBarPinned ? actionBarFrame.width : '100%',
                  p: 1,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: isActionBarPinned ? 'divider' : 'transparent',
                  backgroundColor: isActionBarPinned
                    ? alpha(theme.palette.background.paper, 0.92)
                    : 'transparent',
                  backdropFilter: isActionBarPinned ? 'blur(6px)' : 'none',
                  boxShadow: isActionBarPinned ? theme.shadows[4] : 'none',
                  transition:
                    'background-color 180ms ease, box-shadow 180ms ease, transform 180ms ease, border-color 180ms ease',
                  animation: isActionBarPinned ? 'benefitsStickyReveal 180ms ease-out' : 'none',
                  '@keyframes benefitsStickyReveal': {
                    from: {
                      opacity: 0.92,
                      transform: 'translateY(-4px)',
                    },
                    to: {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }}
              >
                <Controller
                  name="benefitType"
                  control={hook.control}
                  render={({ field }) => (
                    <Box
                      sx={{
                        p: 0.6,
                        borderRadius: 1.5,
                        backgroundColor: 'grey.100',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        display: 'inline-flex',
                        gap: 0.8,
                      }}
                    >
                      {[
                        { value: 'coupon' as const, label: 'Cupom', icon: 'mdi:ticket-outline' },
                        { value: 'product' as const, label: 'Produto', icon: 'mdi:cube-outline' },
                      ].map((option) => {
                        const selected = field.value === option.value;

                        return (
                          <Button
                            key={option.value}
                            variant={selected ? 'contained' : 'text'}
                            color={selected ? 'inherit' : 'inherit'}
                            disabled={hook.isEdit}
                            onClick={() => field.onChange(option.value)}
                            startIcon={
                              <Iconify icon={option.icon} sx={{ width: 18, height: 18 }} />
                            }
                            sx={{
                              minWidth: 120,
                              px: 2,
                              borderRadius: 1,
                              color: selected ? 'text.primary' : 'text.secondary',
                              backgroundColor: selected ? 'common.white' : 'transparent',
                              boxShadow: selected ? 1 : 'none',
                              '&:hover': {
                                backgroundColor: selected ? 'common.white' : 'grey.200',
                              },
                            }}
                          >
                            {option.label}
                          </Button>
                        );
                      })}
                    </Box>
                  )}
                />

                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Ativo
                  </Typography>
                  <Controller
                    name="status"
                    control={hook.control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value === 'active'}
                        onChange={(_, checked) => field.onChange(checked ? 'active' : 'inactive')}
                      />
                    )}
                  />
                </Stack>
              </Stack>
            </Box>

            {isVivaEnv ? (
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  variant="soft"
                  onClick={() => hook.setOpenRedeemModal(true)}
                  disabled={
                    (hook.isEdit && !permissions?.update) || (!hook.isEdit && !permissions?.create)
                  }
                >
                  {hook.currentRedeem ? 'Associar Outro Resgatável' : 'Associar Resgatável'}
                </Button>
              </Stack>
            ) : null}

            {hook.currentRedeem && (
              <Box
                sx={{
                  width: '100%',
                  p: 1,
                  borderRadius: 1,
                  border: '1px dashed',
                  borderColor: 'white',
                  backgroundColor: alpha(theme.palette.primary.main, 0.88),
                }}
              >
                <Stack spacing={0.5}>
                  <Typography variant="body2" sx={{ color: 'common.white', fontWeight: 700 }}>
                    Este benefício está associado a um resgatável. Use os ícones de cópia para
                    preencher alguns campos automaticamente.
                  </Typography>
                  {selectedPartnerName ? (
                    <Typography variant="body2" sx={{ color: 'common.white' }}>
                      Parceiro associado: <strong>{selectedPartnerName}</strong>
                    </Typography>
                  ) : null}
                  {selectedRedeemName ? (
                    <Typography variant="body2" sx={{ color: 'common.white' }}>
                      Resgatável associado: <strong>{selectedRedeemName}</strong>
                    </Typography>
                  ) : null}
                </Stack>
              </Box>
            )}

            {renderDisplayCard()}

            <Card>
              <CardContent>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <SectionTitle index={2} title="Informações" />
                  <IconButton onClick={() => toggleSection('info')}>
                    {expandedSections.info ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>
                </Stack>
                <Collapse in={expandedSections.info} timeout="auto" unmountOnExit={false}>
                  <Box sx={{ pt: 1 }}>
                    {isCoupon ? renderCouponInformationForm() : renderProductInformationForm()}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>

            {!isParkingEnv && renderRedeemCard()}

            {!isParkingEnv && renderFeedbackCard()}

            <Box
              ref={submitActionAnchorRef}
              sx={{ minHeight: isSubmitActionPinned ? `${submitActionFrame.height}px` : 'auto' }}
            >
              <Stack
                component={m.div}
                ref={submitActionBarRef}
                alignItems="flex-end"
                sx={{
                  position: isSubmitActionPinned ? 'fixed' : 'relative',
                  left: isSubmitActionPinned ? submitActionFrame.left : 'auto',
                  width: isSubmitActionPinned ? submitActionFrame.width : '100%',
                  bottom: isSubmitActionPinned ? submitFloatingBottom : 'auto',
                  px: isSubmitActionPinned ? 1 : 0,
                  zIndex: (currentTheme) => currentTheme.zIndex.appBar,
                  transition: 'box-shadow 180ms ease, transform 180ms ease',
                  animation: isSubmitActionPinned
                    ? 'benefitsSubmitPinnedReveal 180ms ease-out'
                    : 'none',
                  '@keyframes benefitsSubmitPinnedReveal': {
                    from: {
                      opacity: 0.92,
                      transform: 'translateY(6px)',
                    },
                    to: {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }}
              >
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={hook.formState.isSubmitting}
                  disabled={
                    (hook.isEdit && !permissions?.update) || (!hook.isEdit && !permissions?.create)
                  }
                >
                  {hook.isEdit ? 'Editar Benefício' : 'Cadastrar Benefício'}
                </LoadingButton>
              </Stack>
            </Box>
          </Stack>
        </Grid>

        <Grid
          item
          xs={12}
          lg={4}
          sx={{
            alignSelf: 'flex-start',
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <Box
            sx={{
              position: 'fixed',
              top: 100,
              ml: { lg: 'clamp(8px, 1.5vw, 28px)', xl: 'clamp(40px, 5vw, 140px)' },
              zIndex: 10,
            }}
          >
            <LivePreviewPanel
              control={hook.control}
              themeOptions={hook.THEME_OPTIONS}
              companyOptions={hook.COMPANY_OPTIONS}
              locationOptions={hook.LOCATIONS_OPTIONS}
            />
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
